import { GroupType } from '@kcftech/prometheus-client';
import { type IndicatorSource } from 'custom_hooks/data_hooks/indicator/shared';
import { getIndicatorFromAlert } from 'pages/Location/alerts_and_drafts/assets_with_alerts/alert_item/utils';
import {
    AlertSeverity,
    type AlertSeverityType,
    getAlertSeverity,
} from 'utils/alert_severity';
import {
    partitionedEnrichedLookup,
    partitionedLookup,
    toEnrichedLookup,
    toLookup,
    toMap,
} from 'utils/array';
import { findInHierarchy } from 'utils/group_hierarchy';
import {
    type AlertAction,
    type AlertActionPatchOp,
    type AlertDragPatchOp,
} from '../alert_moving/useAlertLinkPatch';
import {
    type NeedsAttentionCard,
    type QueueAlert,
    type QueueIssueItem,
} from '../data/queueTypes';

// ---
// Types
// ---

export type IssueId = string;
type AssetId = string;
type MpId = string;
type IndicatorId = string;

export type IndicatorAlertGroup = {
    indicatorId: IndicatorId;
    indicatorName: string;
    /** the backend `indicatorId` belongs to */
    source: IndicatorSource;
    severities: AlertSeverityType[];
    alertIds: string[];
    latestAlertTime: Date | undefined;
    /** whether every Alert in the group is acknowledged */
    isAcknowledged: boolean;
};

export type MpAlertGroup = {
    mpId: MpId;
    mpName: string;
    indicators: IndicatorAlertGroup[];
};

export type AssetGroup = {
    assetId: AssetId;
    assetName: string;
    issues: QueueIssueItem[];
    alertsByMp: MpAlertGroup[];
};

// ---
// Grouping logic
// ---

const SEVERITY_ORDER: AlertSeverityType[] = [
    AlertSeverity.CRITICAL,
    AlertSeverity.ALARM,
    AlertSeverity.WARNING,
];

function groupIndicators(alerts: QueueAlert[]): IndicatorAlertGroup[] {
    const alertsByIndicatorId = toEnrichedLookup(alerts, {
        ctx: (a) => getIndicatorFromAlert(a),
        key: (ctx) => ctx.id,
        info: (ctx) => ({ name: ctx.name, source: ctx.source }),
    });

    return alertsByIndicatorId
        .entries()
        .map(([indicatorId, { info, items }]) => {
            const severitySet = new Set(items.map((a) => getAlertSeverity(a.AlertType)));
            const latestAlertTime = items.reduce<Date | undefined>((latest, a) => {
                const t = a.TimeOfAlert;
                return !latest || t > latest ? t : latest;
            }, undefined);
            return {
                indicatorId,
                indicatorName: info.name,
                source: info.source,
                severities: SEVERITY_ORDER.filter((s) => severitySet.has(s)),
                alertIds: items.map((a) => a.Id),
                latestAlertTime,
                isAcknowledged: items.every((a) => a.IsAcknowledged),
            };
        })
        .toArray();
}

// When an Alert's hierarchy does not contain a Monitoring Point, it must
// be an Analytics Model Alert.
const ANALYTICS_MODEL_MP = {
    HierarchyId: 'Analytics Model Alerts',
    HierarchyName: 'Analytics Model Alerts',
};

function groupAlertsByMp(alerts: QueueAlert[]): MpAlertGroup[] {
    const alertsByMpId = toEnrichedLookup(alerts, {
        ctx: (a) =>
            findInHierarchy(a.Hierarchy, GroupType.MonitoringPoint) ?? ANALYTICS_MODEL_MP,
        key: (ctx) => ctx.HierarchyId,
        info: (ctx) => ctx.HierarchyName,
    });

    return alertsByMpId
        .entries()
        .map(([mpId, { info: mpName, items }]) => ({
            mpId,
            mpName,
            indicators: groupIndicators(items),
        }))
        .toArray();
}

const UNBOUND_ISSUE = '';
const NO_ASSET = { HierarchyId: '', HierarchyName: '' };

export type GroupedCardDetail = {
    /** Assembly-level Issues for display in the left column */
    assemblyIssues: QueueIssueItem[];
    /** unbound alerts at the assembly leve. Rare, exclusively for Analytics Model Alerts */
    assemblyAlerts: MpAlertGroup[];
    /** Asset-level groupings of Issues (left col) and Alerts (right col)  */
    assetGroups: AssetGroup[];
    /** for enriching `IssueRow` with alerts, regardless of where they are */
    alertsByIssueId: Map<IssueId, MpAlertGroup[]>;
};

// Groups a card's Issues[] and Alerts[] into the detail view's grouped shape:
//
//   card.Issues > assemblyIssues           (no asset in hierarchy)
//               > assetGroups[].issues     (grouped by asset)
//
//   card.Alerts > alertsByIssueId          (bound to an issue, grouped by MP)
//               > assemblyAlerts           (unbound, no asset -- e.g. analytics model)
//               > assetGroups[].alertsByMp (unbound, grouped by asset then MP)
export function groupCardDetail(card: NeedsAttentionCard): GroupedCardDetail {
    const { groups: rawAlertsByIssueId, remainder: unboundAlerts } = partitionedLookup(
        card.Alerts,
        {
            key: (a) => a.IssueId ?? UNBOUND_ISSUE,
            sentinel: UNBOUND_ISSUE,
        },
    );

    const alertsByIssueId = toMap(
        rawAlertsByIssueId,
        ([id]) => id,
        ([, alerts]) => groupAlertsByMp(alerts),
    );

    const { groups: issuesByAssetId, remainder: assemblyIssues } =
        partitionedEnrichedLookup(card.Issues, {
            ctx: (item) =>
                findInHierarchy(item.Issue.Hierarchy, GroupType.Asset) ?? NO_ASSET,
            key: (ctx) => ctx.HierarchyId,
            info: (ctx) => ctx.HierarchyName,
            sentinel: NO_ASSET.HierarchyId,
        });

    const { groups: unboundAlertsByAssetId, remainder: assemblyAlerts } =
        partitionedEnrichedLookup(unboundAlerts, {
            ctx: (a) => findInHierarchy(a.Hierarchy, GroupType.Asset) ?? NO_ASSET,
            key: (ctx) => ctx.HierarchyId,
            info: (ctx) => ctx.HierarchyName,
            sentinel: NO_ASSET.HierarchyId,
        });

    const allAssetIds = new Set([
        ...issuesByAssetId.keys(),
        ...unboundAlertsByAssetId.keys(),
    ]);
    const assetGroups = allAssetIds
        .values()
        .map((assetId) => {
            const issueGroup = issuesByAssetId.get(assetId);
            const alertGroup = unboundAlertsByAssetId.get(assetId);

            return {
                assetId,
                assetName: issueGroup?.info ?? alertGroup?.info ?? '',
                issues: issueGroup?.items ?? [],
                alertsByMp: groupAlertsByMp(alertGroup?.items ?? []),
            };
        })
        .toArray();

    return {
        assemblyIssues,
        assetGroups,
        assemblyAlerts: groupAlertsByMp(assemblyAlerts),
        alertsByIssueId,
    };
}

/** An Indicator's Alerts as listed in the card detail, along with where they are listed. */
export type LocatedIndicatorGroup = {
    /** the Issue the group is listed under, or `null` when unlinked */
    issueId: IssueId | null;
    mpId: MpId;
    group: IndicatorAlertGroup;
};

/**
 * Finds the Indicator group containing `alertId` in `grouped`, or `undefined`
 * when no group contains it.
 */
export function findIndicatorGroup(
    grouped: GroupedCardDetail,
    alertId: string,
): LocatedIndicatorGroup | undefined {
    const located = [
        ...grouped.alertsByIssueId.entries().map(([issueId, mps]) => ({ issueId, mps })),
        { issueId: null, mps: grouped.assemblyAlerts },
        ...grouped.assetGroups.map((asset) => ({ issueId: null, mps: asset.alertsByMp })),
    ];
    for (const { issueId, mps } of located) {
        for (const mp of mps) {
            const group = mp.indicators.find((ind) => ind.alertIds.includes(alertId));
            if (group) {
                return { issueId, mpId: mp.mpId, group };
            }
        }
    }
    return undefined;
}

/**
 * Groups the Alerts staged for each action, for previewing them.
 */
export function groupActionAlerts(
    alerts: QueueAlert[],
    actionPatches: AlertActionPatchOp[],
): Map<AlertAction, AlertAssetGroup[]> {
    const alertsById = toMap(alerts, (a) => a.Id);
    const patchesByAction = toLookup(actionPatches, (p) => p.action);

    // Staged Alerts may no longer be on the card after a refetch (patches are
    // only cleared once the refetched card has rendered), so they are skipped,
    // the same as link patches in `applyAlertStatePatch`.
    return toMap(
        patchesByAction.entries(),
        ([action]) => action,
        ([, patches]) =>
            groupAlertsByAsset(
                patches
                    .map((p) => alertsById.get(p.alertId))
                    .filter((a) => a !== undefined),
            ),
    );
}

export type AlertAssetGroup = {
    /** empty for Alerts without an Asset in their hierarchy, e.g. Analytics Model Alerts */
    assetId: AssetId;
    assetName: string;
    alertsByMp: MpAlertGroup[];
};

/**
 * Groups Alerts as Asset > Monitoring Point > Indicator.
 */
export function groupAlertsByAsset(alerts: QueueAlert[]): AlertAssetGroup[] {
    return toEnrichedLookup(alerts, {
        ctx: (a) => findInHierarchy(a.Hierarchy, GroupType.Asset) ?? NO_ASSET,
        key: (ctx) => ctx.HierarchyId,
        info: (ctx) => ctx.HierarchyName,
    })
        .entries()
        .map(([assetId, { info: assetName, items }]) => ({
            assetId,
            assetName,
            alertsByMp: groupAlertsByMp(items),
        }))
        .toArray();
}

// ---
// Patch application
// ---

export function applyAlertStatePatch(
    alerts: QueueAlert[],
    patches: AlertDragPatchOp[],
): QueueAlert[] {
    if (patches.length === 0) {
        return alerts;
    }
    const patchMap = new Map(patches.map((p) => [p.alertId, p]));

    return alerts.map((item) => {
        const patch = patchMap.get(item.Id);
        if (!patch) {
            return item;
        }

        return { ...item, IssueId: patch.op === 'link' ? patch.issueId : undefined };
    });
}
